import React, { useMemo, useState } from "react";
import { ByDayStats } from "../../../types/form-generation-usage.types";
import { format, parseISO } from "date-fns";

interface DailyTrendChartProps {
  data: ByDayStats[];
}

const formatDate = (dateStr: string): string => {
  try {
    const date = parseISO(dateStr);
    return format(date, "MMM d");
  } catch {
    return dateStr;
  }
};

export const DailyTrendChart: React.FC<DailyTrendChartProps> = ({ data }) => {
  const [activeTooltip, setActiveTooltip] = useState<number | null>(null);

  const { maxCount, bars } = useMemo(() => {
    if (!data.length) return { maxCount: 0, bars: [] };

    const max = Math.max(...data.map((d) => d.count));

    const calculatedBars = data.map((item) => ({
      ...item,
      percentage: max > 0 ? (item.count / max) * 100 : 0,
    }));

    return { maxCount: max, bars: calculatedBars };
  }, [data]);

  if (!data.length) {
    return (
      <div className="bg-white rounded-lg border border-neutral-200 p-6">
        <h3 className="text-lg font-semibold text-neutral-900 mb-4">
          Generacje dziennie
        </h3>
        <div className="text-neutral-500 text-center py-8">
          Brak danych dla wybranego okresu
        </div>
      </div>
    );
  }

  return (
    <div
      className="bg-white rounded-lg border border-neutral-200 p-6"
      data-testid="chart-daily-trend"
      role="figure"
      aria-label="Wykres dziennych generacji"
    >
      <h3 className="text-lg font-semibold text-neutral-900 mb-4">
        Generacje dziennie
      </h3>

      <div className="relative flex gap-1 h-48">
        {/* Y-axis */}
        <div className="flex flex-col justify-between text-xs text-neutral-500 pr-2 py-0.5">
          <span>{maxCount}</span>
          <span>{Math.round(maxCount / 2)}</span>
          <span>0</span>
        </div>

        {/* Chart area */}
        <div className="flex-1 relative h-full">
          {/* Bars */}
          <div className="flex items-end gap-1 h-full">
            {bars.map((bar, index) => (
              <div
                key={bar.date}
                className="flex-1 h-full flex flex-col justify-end items-center relative"
                data-testid={`bar-${index}`}
              >
                {/* Tooltip */}
                <div
                  className={`absolute bottom-full mb-2 z-10 transition-opacity ${
                    activeTooltip === index
                      ? "opacity-100"
                      : "opacity-0 pointer-events-none"
                  }`}
                >
                  <div className="bg-neutral-900 text-white text-xs rounded px-2 py-1 whitespace-nowrap">
                    <div className="font-medium">{formatDate(bar.date)}</div>
                    <div>{bar.count} generacji</div>
                  </div>
                </div>

                {/* Bar */}
                <div
                  tabIndex={0}
                  role="img"
                  aria-label={`${formatDate(bar.date)}: ${bar.count} generacji`}
                  className="w-full bg-primary-500 hover:bg-primary-600 focus:bg-primary-600 transition-colors rounded-t cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-600 focus-visible:ring-offset-2"
                  style={{
                    height: `${Math.max(bar.percentage, 2)}%`,
                  }}
                  onMouseEnter={() => setActiveTooltip(index)}
                  onMouseLeave={() => setActiveTooltip(null)}
                  onFocus={() => setActiveTooltip(index)}
                  onBlur={() => setActiveTooltip(null)}
                  onTouchStart={() => setActiveTooltip(index)}
                />
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* X-axis labels */}
      <div className="flex justify-between mt-2 text-xs text-neutral-500 ml-12">
        {bars.length > 0 && (
          <>
            <span>{formatDate(bars[0].date)}</span>
            {bars.length > 2 && (
              <span>
                {formatDate(bars[Math.floor(bars.length / 2)].date)}
              </span>
            )}
            {bars.length > 1 && (
              <span>{formatDate(bars[bars.length - 1].date)}</span>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default DailyTrendChart;
